import { Revision } from "@common/utils/redline/ooxml/revision";
import { NO_TABLE_CHANGES, type TableOperationsApplyResult } from "@common/utils/redline/table-operations";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import type { ProposalCardContent } from "./card-content";

export type ProposalCardKind = "redline" | "changeThroughout" | "revert" | "revertOwn" | "table" | "tableRows" | "tableRowDeletion" | "newTable" | "paragraph" | "comment" | "reply";

export interface ProposalCardIdentity {
  cardNumber: number | undefined;
  kind: ProposalCardKind;
}

export const PROPOSAL_CARD_KIND_LABELS: Record<ProposalCardKind, string> = {
  redline: "Redline",
  changeThroughout: "Change throughout",
  revert: "Revert counterparty tracked change",
  revertOwn: "Revert own tracked change",
  table: "Table edit",
  tableRows: "New row",
  tableRowDeletion: "Deleted row",
  newTable: "New table",
  paragraph: "New paragraph",
  comment: "Comment",
  reply: "Reply",
};

const TABLE_CARD_KINDS: ReadonlySet<ProposalCardKind> = new Set<ProposalCardKind>(["table", "tableRows", "tableRowDeletion", "newTable"]);

export const isTableCardKind = (kind: ProposalCardKind): boolean => TABLE_CARD_KINDS.has(kind);

export function tableCardKindOf(tableChanges: TableOperationsApplyResult): ProposalCardKind {
  const operationKinds = [tableChanges.tableInsertions.length > 0, tableChanges.rowInsertions.length > 0, tableChanges.rowDeletions.length > 0].filter(Boolean).length;
  if (operationKinds !== 1) return "table";
  if (tableChanges.tableInsertions.length > 0) return "newTable";
  if (tableChanges.rowInsertions.length > 0) return "tableRows";
  return "tableRowDeletion";
}

const revertsSomething = (textRedline: RedlineTextProposal | null): textRedline is RedlineTextProposal => (textRedline?.reverts.length ?? 0) > 0;

const revertCardKindOf = (textRedline: RedlineTextProposal): ProposalCardKind =>
  textRedline.reverts.every((revert) => Revision.sameAuthor(textRedline.author, revert.expectedChange.author)) ? "revertOwn" : "revert";

export function proposalCardKindOf(toolName: string | undefined, textRedline: RedlineTextProposal | null): ProposalCardKind {
  if (toolName === "add_comment") return "comment";
  if (toolName === "reply_to_comment") return "reply";
  if (toolName === "add_paragraph") return "paragraph";
  if (toolName === "edit_table" || textRedline?.cardKind === "table") return tableCardKindOf(textRedline?.tableChanges ?? NO_TABLE_CHANGES);
  if (revertsSomething(textRedline)) return revertCardKindOf(textRedline);
  return "redline";
}

export function proposalCardKindOfContent(content: ProposalCardContent): ProposalCardKind {
  if (content.kind === "table") return tableCardKindOf(content.textRedline?.tableChanges ?? NO_TABLE_CHANGES);
  if (content.kind === "redline" && revertsSomething(content.textRedline)) return revertCardKindOf(content.textRedline);
  return content.kind;
}

export function cardKindReadingItsPlaces(kindOfItsText: ProposalCardKind, changesThroughoutPlaces: boolean): ProposalCardKind {
  if (changesThroughoutPlaces) return "changeThroughout";
  return kindOfItsText;
}

export const proposalCardTitleOf =(cardNumber: number | undefined, kind: ProposalCardKind): string =>
  cardNumber === undefined ? PROPOSAL_CARD_KIND_LABELS[kind] : `#${cardNumber} · ${PROPOSAL_CARD_KIND_LABELS[kind]}`;
